'use client';

import { logErrorMessage } from '@koda/koda-logger/loggers';
import { useEffect, useRef, useState } from 'react';
import useSWR, { SWRConfiguration, SWRResponse } from 'swr';
import { Artist, ArtistsOptions, ArtistsSearchOptions } from '../models/artists';
import { parseArtistsResponse } from '../models/artists.Schema';
import { buildArtistsQuery } from '../utils/searchParams';

export function useArtistsSearch({
  searchString,
  top,
  enabled = true,
  minLength = 1,
  debounceMs = 300,
}: ArtistsSearchOptions): SWRResponse<Artist[], Error, SWRConfiguration> {
  const [debouncedOptions, setDebouncedOptions] = useState<ArtistsOptions | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setDebouncedOptions(null);
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      return;
    }
    const trimmed = (searchString ?? '').trim();
    const meetsMinLength = trimmed.length >= minLength;
    // Only start searching when minLength is met
    if (!meetsMinLength) {
      setDebouncedOptions(null);
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDebouncedOptions({ searchString: trimmed, top });
    }, debounceMs);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [searchString, top, enabled, minLength, debounceMs]);

  const key = debouncedOptions ? `/api/economy/soloists${buildArtistsQuery(debouncedOptions)}` : null;

  return useSWR<Artist[], Error>(
    key,
    async (url): Promise<Artist[]> => {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
      });
      if (!res.ok) {
        throw new Error(`Artists fetch failed (${res.status})`);
      }

      const rawJson: unknown = await res.json().catch(() => null);
      const data = parseArtistsResponse(rawJson);
      if (!data) {
        logErrorMessage(new Error('Artists response did not match expected shape'));
        return [];
      }

      return (data.value || []).map(a => ({
        number: a.number,
        searchName: a.searchName,
        name: a.name,
        value: a.number,
        text: a.name,
      }));
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      dedupingInterval: 2000,
    },
  );
}
